import { StickyNoteConfigurations } from '../items-canvas/board-items/board-sticky-note-configurations';

export enum LINE_TYPE {
  LINE = 'line',
  ARROW = 'arrow',
  DASHED = 'dashed',
}
export class FabricObjectSettings {
  availableStrokeWidths!: number[];
  availableStrokeColorsMap!: { [key: string]: string };
  strokeWidth!: number;
  strokeColor!: string;
  fillColor!: string;
  fill!: string; // use this for Text only to avoid conflicts with Fabric's 'fill' property on Text
  fontWeight!: string;
  fontStyle!: '' | 'normal' | 'italic' | 'oblique' | undefined;
  underline!: boolean;
  textBackgroundColor!: string;
  fontSize!: number;
  fontFamily!: string;
  lineType!: LINE_TYPE;
  backgroundColor!: string; // used for textbox
}

export class FabricObjectSettingsBuilder {
  public static readonly COLOR_PICKER = 'COLOR_PICKER';

  fabricObjectSettings!: FabricObjectSettings;
  colorsMap = {
    '#333333': 'rgba(51, 51, 51, 0.5)',
    '#7E7F9A': 'rgba(126, 127, 154, 0.5)',
    '#33A787': 'rgba(51, 167, 135, 0.5)',
    '#0082f5': 'rgba(47, 128, 237, 0.5)',
    '#E5616D': 'rgba(229, 97, 109, 0.5)',
    '#A36BB2': 'rgba(163, 107, 178, 0.5)',
    '#F2994A': 'rgba(242, 153, 74, 0.5)',
    '#FEE133': 'rgba(254, 225, 51, 0.5)',
    '#FFFFFF': 'rgba(255, 255, 255, 0.5)',
    COLOR_PICKER: FabricObjectSettingsBuilder.COLOR_PICKER,
  };

  constructor() {
    this.fabricObjectSettings = new FabricObjectSettings();
  }

  getFabricObjectSetting(): FabricObjectSettings {
    return this.fabricObjectSettings;
  }

  setAvailableStrokeWidths(fabricObjectType: string) {
    switch (fabricObjectType) {
      case 'pen':
        this.fabricObjectSettings.availableStrokeWidths = [1, 2, 4, 8, 12, 16];
        break;
      case 'highlighter':
        this.fabricObjectSettings.availableStrokeWidths = [2.5, 5, 10, 15, 20, 25];
        break;
    }
    return this;
  }

  setAvailableStrokeColorsMap() {
    this.fabricObjectSettings.availableStrokeColorsMap = this.colorsMap;
    return this;
  }

  setStrokeWidth(strokeWidth: number) {
    this.fabricObjectSettings.strokeWidth = strokeWidth;
    return this;
  }

  setStrokeColor(strokeColor: string) {
    this.fabricObjectSettings.strokeColor = strokeColor;
    return this;
  }

  setFillColor(fillColor: string) {
    this.fabricObjectSettings.fillColor = fillColor;
    return this;
  }

  setFill(fillColor: string) {
    this.fabricObjectSettings.fill = fillColor;
    return this;
  }

  setFontWeight(fontWeight: string) {
    this.fabricObjectSettings.fontWeight = fontWeight;
    return this;
  }

  setFontStyle(fontStyle: '' | 'normal' | 'italic' | 'oblique' | undefined) {
    this.fabricObjectSettings.fontStyle = fontStyle;
    return this;
  }

  setUnderline(isUnderline: boolean) {
    this.fabricObjectSettings.underline = isUnderline;
    return this;
  }

  setTextBackgroundColor(textBackgroundColor: string) {
    this.fabricObjectSettings.textBackgroundColor = textBackgroundColor;
    return this;
  }

  setBackgroundColor(backgroundColor: string) {
    this.fabricObjectSettings.backgroundColor = backgroundColor;
    return this;
  }

  setFontSize(fontSize: number) {
    this.fabricObjectSettings.fontSize = fontSize;
    return this;
  }

  setFontFamily(fontFamily: string) {
    this.fabricObjectSettings.fontFamily = fontFamily;
    return this;
  }

  setLineType(lineType: LINE_TYPE) {
    this.fabricObjectSettings.lineType = lineType;
    return this;
  }
}

export class ShapeSettingsDirector {
  static build() {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeColorsMap()
      .setStrokeWidth(4)
      .setStrokeColor('#333333')
      .setFillColor('transparent')
      .getFabricObjectSetting();
  }
}

export class TextSettingsDirector {
  static build(): FabricObjectSettings {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeColorsMap()
      .setFontWeight('normal')
      .setFontStyle('normal')
      .setUnderline(false)
      .setFill('#333333')
      .setTextBackgroundColor('transparent')
      .setBackgroundColor('transparent')
      .setFontSize(18)
      .setFontFamily('Source Sans Pro')
      .getFabricObjectSetting();
  }
}

export class StickyNoteTextSettingsDirector {
  static build(): FabricObjectSettings {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeColorsMap()
      .setFontWeight(StickyNoteConfigurations.defaultStickyNoteTextSettings().fontWeight)
      .setFontStyle(StickyNoteConfigurations.defaultStickyNoteTextSettings().fontStyle)
      .setUnderline(StickyNoteConfigurations.defaultStickyNoteTextSettings().underline)
      .setFill('#333333')
      .setFontSize(StickyNoteConfigurations.defaultStickyNoteTextSettings().fontSize)
      .setFontFamily(StickyNoteConfigurations.defaultStickyNoteTextSettings().fontFamily)
      .getFabricObjectSetting();
  }
}

export class LineToolbarSettingsDirector {
  static build() {
    return new FabricObjectSettingsBuilder()
      .setLineType(LINE_TYPE.LINE)
      .setStrokeWidth(4)
      .setStrokeColor('#333333')
      .getFabricObjectSetting();
  }
}

export class LineSettingsDirector {
  static build() {
    return new FabricObjectSettingsBuilder()
      .setLineType(LINE_TYPE.LINE)
      .setAvailableStrokeColorsMap()
      .setStrokeWidth(4)
      .setStrokeColor('#333333')
      .getFabricObjectSetting();
  }
}

export class PenScribbleSettingsDirector {
  static build(): FabricObjectSettings {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeWidths('pen')
      .setAvailableStrokeColorsMap()
      .setStrokeWidth(2)
      .setStrokeColor('#0082f5')
      .getFabricObjectSetting();
  }
}

export class HighlighterScribbleSettingsDirector {
  static build(): FabricObjectSettings {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeWidths('highlighter')
      .setAvailableStrokeColorsMap()
      .setStrokeWidth(5)
      .setStrokeColor('#FEE133')
      .getFabricObjectSetting();
  }
}

export class LatexSettingsDirector {
  static build(): FabricObjectSettings {
    return new FabricObjectSettingsBuilder()
      .setAvailableStrokeColorsMap()
      .setFontWeight('normal')
      .setFontStyle('normal')
      .setUnderline(false)
      .setFill('#333333')
      .setTextBackgroundColor('#ffffff')
      .setFontSize(18)
      .setFontFamily('Source Sans Pro')
      .getFabricObjectSetting();
  }
}
